import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "src/assets/svg-icons/aggregators/delete.svg";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import { ConfirmPopup } from "src/shared/components/confirm-popup";
import FilePondUploader from "src/shared/components/file-pond-uploader";
import { notificationsState } from "src/store/notifications";
import { APIErrorResponse } from "src/types/generic";

export interface AttachmentRow {
  id: number;
  file: {
    id: number;
    name: string | null;
    url?: string;
  } | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  entityId: number | null;
  entityLabel?: string;
  attachments: AttachmentRow[];
  isLoading: boolean;
  refetch: () => void;
  onUpload: (file: File) => void;
  uploading: boolean;
  onRemove: (
    attachmentId: number,
    callbacks: {
      onSuccess: () => void;
      onError: (err: APIErrorResponse) => void;
    },
  ) => void;
  removing: boolean;
}

const AttachmentsDialog = ({
  open,
  onClose,
  entityId,
  entityLabel,
  attachments,
  isLoading,
  refetch,
  onUpload,
  uploading,
  onRemove,
  removing,
}: Props) => {
  const { t: generalT } = useTranslation("general");
  const { t } = useTranslation("salary");
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const prevPondFilesRef = useRef<File[]>([]);
  const [pondKey, setPondKey] = useState(0);
  const [pendingDelete, setPendingDelete] = useState<{
    id: number;
    label: string;
  } | null>(null);

  useEffect(() => {
    if (open && entityId) {
      refetch();
    }
  }, [open, entityId, refetch]);

  useEffect(() => {
    if (open) {
      prevPondFilesRef.current = [];
      setPondKey((k) => k + 1);
    }
  }, [open, entityId]);

  const onPondUpload = (files: File[]) => {
    if (!entityId) return;
    const prev = prevPondFilesRef.current;
    prevPondFilesRef.current = files;
    const prevKeys = new Set(
      prev.map((f) => `${f.name}-${f.size}-${f.lastModified}`),
    );
    const added = files.filter(
      (f) => !prevKeys.has(`${f.name}-${f.size}-${f.lastModified}`),
    );
    added.forEach((file) => {
      onUpload(file);
    });
  };

  const confirmDelete = () => {
    if (pendingDelete == null || !entityId) return;
    onRemove(pendingDelete.id, {
      onSuccess: () => {
        setPendingDelete(null);
        setNotifications([
          ...notifications,
          { type: "success", message: generalT("deletedSuccessfully") },
        ]);
      },
      onError: (err: APIErrorResponse) => {
        setNotifications([
          ...notifications,
          {
            type: "error",
            message: err?.data?.errors?.[0]?.value ?? generalT("somethingWrong"),
          },
        ]);
      },
    });
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography fontSize="18px" fontWeight={600}>
            {generalT("attachments")}
            {entityLabel ? ` — ${entityLabel}` : ""}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {!uploading && !!entityId && (
            <FilePondUploader
              key={pondKey}
              onUpload={onPondUpload}
              maxFiles={3}
              maxFileSize={3}
              acceptedFileTypes={[
                "image/*",
                "application/pdf",
                "text/csv",
                "application/vnd.ms-excel",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              ]}
            />
          )}
          <Stack spacing={2} mt={2}>
            {isLoading ? (
              <Typography color="#98A2B3">{generalT("loading")}…</Typography>
            ) : attachments.length === 0 ? (
              <Typography color="#98A2B3">{t("noAttachments")}</Typography>
            ) : (
              attachments.map((row) => (
                <Stack
                  key={row.id}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={1}
                  py={1}
                  borderBottom="1px solid #EAECF0"
                >
                  <Box>
                    {row.file?.url ? (
                      <Link
                        href={row.file.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {row.file.name}
                      </Link>
                    ) : (
                      <Typography>{row.file?.name ?? "—"}</Typography>
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setPendingDelete({
                        id: row.id,
                        label: row.file?.name ?? "",
                      })
                    }
                    aria-label={generalT("delete")}
                  >
                    <img src={DeleteIcon} alt="" width={20} />
                  </IconButton>
                </Stack>
              ))
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("close")}</Button>
        </DialogActions>
      </Dialog>

      <ConfirmPopup
        open={pendingDelete !== null}
        handleClose={() => setPendingDelete(null)}
        handleConfirm={confirmDelete}
        isPending={removing}
        title={generalT("delete")}
        subtitle={generalT("AreYouWantToDelete")}
        item={pendingDelete?.label}
      />
    </>
  );
};

export default AttachmentsDialog;
